/**
 * Relative-address resolution for `DataRef` / `ComputedDataSource` rows.
 *
 * A generic `ComputedDataSource` row declares a relative `localPath` instead
 * of an absolute `lookupKey`; its concrete key is resolved from where it sits
 * in the tree — `nearestKeyedAncestor.lookupKey + "." + localPath` (the rowkey
 * `.`-derivation). This makes a derived row **portable** (it re-derives its
 * identity under any parent) and **concretely addressable** (others reference
 * the resolved key, reused via `canonicalCells`).
 *
 * Design: lona-sdk-2.0.project.docs/content/data-address/adoption.md
 * (Decisions OQ1 / OQ5). The resolver is pure and tree-shaped so it is
 * testable without the sheet engine and reusable by both computed-row
 * hydration and any consumer that holds a relative ref.
 *
 * NOTE on chaining: a derived row that is itself an ancestor (e.g.
 * `:~garmin:sleep.deep`) must publish its *resolved* key into its
 * `lookupKey` so a deeper child resolves to `:~garmin:sleep.deep.smoothed`.
 * That write-back is the `ComputedDataSource` hydration step (a later slice);
 * this primitive only walks the keys that are present.
 */

import type { Option } from "../core/option.ts";

/**
 * The minimal tree node the resolver walks — satisfied by `ModelRow`
 * (`get parent`, `get lookupKey`). Kept structural so the resolver has no
 * dependency on the concrete row classes (and is trivially mockable in tests).
 */
export interface LookupKeyNode {
  readonly lookupKey: Option<string>;
  readonly parent: Option<LookupKeyNode>;
}

/**
 * The rowkey derivation separator: `:~lona:task` + `due` ⇒ `:~lona:task.due`.
 * A printable, unambiguous ASCII char that is already the canonical
 * `.`-derivation marker in the rowkey grammar (Part 8).
 */
export const LOOKUP_KEY_DERIVATION_SEP = ".";

/**
 * Resolve a relative `localPath` to a concrete lookupKey by walking up to the
 * nearest ancestor that carries a non-empty `lookupKey` and appending the
 * segment. Unset when no ancestor carries a key — the row is not
 * yet addressable, and the caller treats it as unresolved (never throws).
 *
 * The walk starts at the node's *parent*: a row declaring a relative
 * `localPath` owns no absolute key of its own, so its identity is derived from
 * the surrounding context, not from itself.
 */
export function resolveRelativeLookupKey(
  node: LookupKeyNode,
  localPath: string,
): Option<string> {
  for (let cur = node.parent; cur != null; cur = cur.parent) {
    const key = cur.lookupKey;
    if (key != null && key.length > 0) {
      return `${key}${LOOKUP_KEY_DERIVATION_SEP}${localPath}`;
    }
  }
  return null;
}
